import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { HiOutlineChevronRight } from 'react-icons/hi2';

interface CollapsibleSectionProps {
  title: string;
  children: ReactNode;
  /** When true, keep showing the spinner (data still loading). */
  isLoading?: boolean;
  spinnerLabel?: string;
}

/**
 * Chevron-collapsible section, collapsed by default. On expand it shows a brief
 * spinner before mounting its (heavy chart) children, so opening it doesn't jank.
 * Chevron points right when collapsed, rotates down when expanded (repo convention).
 */
export default function CollapsibleSection({
  title,
  children,
  isLoading = false,
  spinnerLabel = 'Loading…',
}: CollapsibleSectionProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setReady(false);
      return undefined;
    }
    setReady(false);
    const id = window.setTimeout(() => setReady(true), 250);
    return () => window.clearTimeout(id);
  }, [open]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="cr-flex cr-items-center cr-gap-2 cr-text-lg cr-font-semibold cr-text-blue-900"
      >
        <HiOutlineChevronRight
          className={`cr-h-4 cr-w-4 cr-transition-transform ${open ? 'cr-rotate-90' : ''}`}
        />
        {title}
      </button>
      {open && (
        !ready || isLoading ? (
          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-gap-2 cr-text-gray-400">
            <div
              className="cr-h-6 cr-w-6 cr-animate-spin cr-rounded-full cr-border-2
                cr-border-cirrusBlue-200 cr-border-t-cirrusBlue-400"
            />
            <span className="cr-text-sm">{spinnerLabel}</span>
          </div>
        ) : (
          <div className="cr-mt-4 cr-space-y-6">{children}</div>
        )
      )}
    </div>
  );
}
